import { useState, useEffect, useCallback } from 'react';
import { settingsAPI, userAPI, zohoAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Settings as SettingsIcon, Bot, Shield, Percent, Wallet, Gauge, RefreshCw, Mail, Send, Lock, Download } from 'lucide-react';

const EMAIL_STATUS_STYLES = {
  SENT: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  FAILED: 'bg-red-500/15 text-red-600 border-red-500/30',
  RESENT: 'bg-sky-500/15 text-sky-600 border-sky-500/30',
  SKIPPED_ALLOWLIST: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  SKIPPED_NOT_CONFIGURED: 'bg-slate-500/15 text-slate-600 border-slate-500/30',
  LOGGED_PREVIEW: 'bg-violet-500/15 text-violet-600 border-violet-500/30',
};

export default function SettingsPage() {
  const { canAccess, isSuperAdmin } = useAuth();
  const canEditSettings = canAccess('SETTINGS', 'EDIT');
  const [settings, setSettings] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [soResyncLoading, setSoResyncLoading] = useState(false);
  const [soResyncResult, setSoResyncResult] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [emailLogFilters, setEmailLogFilters] = useState({ status: '__all__', kind: '__all__', notification_type: '' });
  const [resendingId, setResendingId] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [formData, setFormData] = useState({
    tolerance_percent: 3,
    assumed_remaining_qty: 100,
    ai_enabled: true,
    ai_default_model: 'gpt-4o-mini',
    openai_api_key: '',
    vendor_payment_final_approver_user_id: '',
    vendor_payment_extraction_model: 'gemini-3.1-pro-preview',
    zoho_po_detail_cap_stopgap: true,
    pms_view_all_emails: '',
    // Screen Capture & Export Protection (4 Nov 2026)
    export_allowed_user_ids: [],
    capture_blur_enabled: true,
    network_lock_enabled: false,
    allowed_networks: '',
    network_lock_exempt_user_ids: [],
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
      setFormData({
        tolerance_percent: response.data.tolerance_percent,
        assumed_remaining_qty: response.data.assumed_remaining_qty,
        ai_enabled: response.data.ai_enabled,
        ai_default_model: response.data.ai_default_model,
        openai_api_key: '',
        vendor_payment_final_approver_user_id: response.data.vendor_payment_final_approver_user_id || '',
        vendor_payment_extraction_model: response.data.vendor_payment_extraction_model || 'gemini-3.1-pro-preview',
        zoho_po_detail_cap_stopgap: response.data.zoho_po_detail_cap_stopgap !== false,
        pms_view_all_emails: (response.data.pms_view_all_emails || []).join(', '),
        export_allowed_user_ids: response.data.export_allowed_user_ids || [],
        capture_blur_enabled: response.data.capture_blur_enabled !== false,
        network_lock_enabled: response.data.network_lock_enabled === true,
        allowed_networks: (response.data.allowed_networks || []).join(', '),
        network_lock_exempt_user_ids: response.data.network_lock_exempt_user_ids || [],
      });
      if (canEditSettings) {
        const usersRes = await userAPI.list({ limit: 200 });
        setActiveUsers((usersRes.data.users || []).filter(u => u.is_active));
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [canEditSettings]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        tolerance_percent: parseFloat(formData.tolerance_percent),
        assumed_remaining_qty: parseInt(formData.assumed_remaining_qty),
        ai_enabled: formData.ai_enabled,
        ai_default_model: formData.ai_default_model,
      };

      if (canEditSettings) {
        updateData.vendor_payment_extraction_model = formData.vendor_payment_extraction_model;
      }
      // 21 Oct 2026 security hardening (item 5): these three are Super-Admin-only — the
      // backend rejects them from anyone else, so don't even send them from the UI.
      if (isSuperAdmin) {
        updateData.vendor_payment_final_approver_user_id = formData.vendor_payment_final_approver_user_id;
        updateData.zoho_po_detail_cap_stopgap = formData.zoho_po_detail_cap_stopgap;
        updateData.pms_view_all_emails = formData.pms_view_all_emails
          .split(',').map(e => e.trim()).filter(Boolean);
        updateData.export_allowed_user_ids = formData.export_allowed_user_ids;
        updateData.capture_blur_enabled = formData.capture_blur_enabled;
        updateData.network_lock_enabled = formData.network_lock_enabled;
        updateData.allowed_networks = formData.allowed_networks
          .split(',').map(n => n.trim()).filter(Boolean);
        updateData.network_lock_exempt_user_ids = formData.network_lock_exempt_user_ids;
      }
      
      // Only send API key if it was changed
      if (formData.openai_api_key) {
        updateData.openai_api_key = formData.openai_api_key;
      }
      
      await settingsAPI.update(updateData);
      toast.success('Settings saved');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserInList = (field, userId) => {
    setFormData(prev => {
      const list = prev[field] || [];
      return {
        ...prev,
        [field]: list.includes(userId) ? list.filter(id => id !== userId) : [...list, userId],
      };
    });
  };

  const handleSoResync = async (dryRun) => {
    setSoResyncLoading(true);
    try {
      const response = await zohoAPI.soResyncSince('2026-02-01', dryRun);
      setSoResyncResult(response.data);
      toast.success(dryRun ? 'Dry-run complete — see numbers below' : 'SO catch-up started — headers upserted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SO resync failed');
    } finally {
      setSoResyncLoading(false);
    }
  };

  const fetchEmailLog = useCallback(async () => {
    if (!isSuperAdmin) return;
    setEmailLogLoading(true);
    try {
      const params = {};
      if (emailLogFilters.status !== '__all__') params.status = emailLogFilters.status;
      if (emailLogFilters.kind !== '__all__') params.kind = emailLogFilters.kind;
      if (emailLogFilters.notification_type.trim()) params.notification_type = emailLogFilters.notification_type.trim();
      const response = await settingsAPI.getEmailLog(params);
      setEmailLog(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load email log');
    } finally {
      setEmailLogLoading(false);
    }
  }, [isSuperAdmin, emailLogFilters]);

  useEffect(() => {
    fetchEmailLog();
  }, [fetchEmailLog]);

  const handleResendEmail = async (id) => {
    setResendingId(id);
    try {
      const response = await settingsAPI.resendEmail(id);
      toast.success(`Resend attempted — status: ${response.data.status}`);
      fetchEmailLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await settingsAPI.sendTestEmail();
      toast.success(`Test email attempted — status: ${response.data.status}`);
      fetchEmailLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Configure system behavior and integrations</p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Approval Settings */}
        <Card className="rounded-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="h-5 w-5 text-primary" />
              Approval Settings
            </CardTitle>
            <CardDescription>
              Configure when purchases require manager approval
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tolerance">Tolerance Percentage (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="tolerance"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={formData.tolerance_percent}
                  onChange={(e) => setFormData(prev => ({ ...prev, tolerance_percent: e.target.value }))}
                  className="w-32 rounded-sm font-mono"
                  data-testid="tolerance-input"
                />
                <Percent className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground">
                Purchases exceeding recommended max by this percentage will require approval
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="assumed_qty">Default Assumed Remaining Qty</Label>
              <Input
                id="assumed_qty"
                type="number"
                min="1"
                value={formData.assumed_remaining_qty}
                onChange={(e) => setFormData(prev => ({ ...prev, assumed_remaining_qty: e.target.value }))}
                className="w-32 rounded-sm font-mono"
                data-testid="assumed-qty-input"
              />
              <p className="text-xs text-muted-foreground">
                Used for guidance calculations when expected quantity is not set
              </p>
            </div>
          </CardContent>
        </Card>

        {/* AI Settings */}
        <Card className="rounded-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bot className="h-5 w-5 text-primary" />
              AI Integration
            </CardTitle>
            <CardDescription>
              Configure ChatGPT integration for offer parsing and insights
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="ai_enabled">Enable AI Features</Label>
                <p className="text-xs text-muted-foreground">
                  Turn on AI-powered offer parsing and insights
                </p>
              </div>
              <Switch
                id="ai_enabled"
                checked={formData.ai_enabled}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, ai_enabled: checked }))}
                data-testid="ai-toggle"
              />
            </div>
            
            {formData.ai_enabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="ai_model">Default Model</Label>
                  <Select
                    value={formData.ai_default_model}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, ai_default_model: value }))}
                  >
                    <SelectTrigger className="w-48 rounded-sm" data-testid="ai-model-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini (Fast)</SelectItem>
                      <SelectItem value="gpt-4o">GPT-4o (Accurate)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    GPT-4o Mini for day-to-day use, GPT-4o for high accuracy mode
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="api_key">Custom OpenAI API Key (Optional)</Label>
                  <Input
                    id="api_key"
                    type="password"
                    value={formData.openai_api_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, openai_api_key: e.target.value }))}
                    placeholder={settings?.has_custom_openai_key ? '••••••••••••••••' : 'sk-...'}
                    className="rounded-sm"
                    data-testid="api-key-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty to use the default API key. Enter your own key for separate billing.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Vendor Payment Settings — Super Admin / Admin only */}
        {canEditSettings && (
          <Card className="rounded-sm" data-testid="vendor-payment-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Wallet className="h-5 w-5 text-primary" />
                Vendor Payment
              </CardTitle>
              <CardDescription>
                Final approver (Super Admin only) and AI extraction model (Admin) for the Vendor Payment approval flow. Every change is recorded in the audit log.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="final_approver">Final Approver {!isSuperAdmin && <span className="text-xs text-muted-foreground font-normal">(Super Admin only)</span>}</Label>
                <Select
                  value={formData.vendor_payment_final_approver_user_id || '__none__'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, vendor_payment_final_approver_user_id: value === '__none__' ? '' : value }))}
                  disabled={!isSuperAdmin}
                >
                  <SelectTrigger className="w-72 rounded-sm" data-testid="vp-final-approver-select">
                    <SelectValue placeholder="Select final approver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not set</SelectItem>
                    {activeUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Every Vendor Payment approval chain ends with this user. Submissions are blocked if this is unset or the selected user is inactive.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vp_model">Gemini Model Name</Label>
                <Input
                  id="vp_model"
                  value={formData.vendor_payment_extraction_model}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor_payment_extraction_model: e.target.value }))}
                  placeholder="gemini-3.1-pro-preview"
                  className="w-72 rounded-sm font-mono"
                  data-testid="vp-extraction-model-input"
                />
                <p className="text-xs text-muted-foreground">
                  Model used to extract line items from uploaded PI documents.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Zoho PO Detail-Sync Stopgap — Super Admin / Admin only */}
        {canEditSettings && (
          <Card className="rounded-sm" data-testid="zoho-po-stopgap-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Gauge className="h-5 w-5 text-primary" />
                Zoho PO Detail-Sync Stopgap
              </CardTitle>
              <CardDescription>
                Caps Purchase Order line-item detail fetches at 1,000/day, independently of Sales Order/Bill/Invoice's own 7,437/day caps. Super Admin only — every change is recorded in the audit log and notifies Lex.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="po_stopgap">PO cap stopgap (1,000/day) {!isSuperAdmin && <span className="text-xs text-muted-foreground font-normal">(Super Admin only)</span>}</Label>
                  <p className="text-xs text-muted-foreground">
                    On by default after the 19 Oct incident. Turn off once PO call volume is confirmed stable near zero.
                  </p>
                </div>
                <Switch
                  id="po_stopgap"
                  checked={formData.zoho_po_detail_cap_stopgap}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, zoho_po_detail_cap_stopgap: checked }))}
                  disabled={!isSuperAdmin}
                  data-testid="zoho-po-stopgap-toggle"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* PMS org-wide visibility allowlist — Super Admin only */}
        {canEditSettings && (
          <Card className="rounded-sm" data-testid="pms-view-all-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="h-5 w-5 text-primary" />
                PMS Org-Wide Visibility
              </CardTitle>
              <CardDescription>
                Reviews are visible to the employee and their manager (direct + indirect reports). Super Admin always sees everyone. Add emails here to grant anyone else that same company-wide view (e.g. an exec who isn't a system Admin) without giving them PMS edit rights. Comma-separated. Super Admin only — notifies Lex on every change.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="pms_view_all_emails">Emails with company-wide PMS visibility {!isSuperAdmin && <span className="text-xs text-muted-foreground font-normal">(Super Admin only)</span>}</Label>
              <Input
                id="pms_view_all_emails"
                data-testid="pms-view-all-emails-input"
                placeholder="e.g. ea1@vstl.hk"
                value={formData.pms_view_all_emails}
                onChange={(e) => setFormData(prev => ({ ...prev, pms_view_all_emails: e.target.value }))}
                disabled={!isSuperAdmin}
              />
            </CardContent>
          </Card>
        )}

        {/* Export & Print Protection (4 Nov 2026) — Super Admin only, both to see and to edit */}
        {isSuperAdmin && (
          <Card className="rounded-sm" data-testid="export-protection-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Download className="h-5 w-5 text-primary" />
                Export & Print Protection
              </CardTitle>
              <CardDescription>
                Being an Admin is no longer enough to export or print Trade IQ data — a user must also be on this list, and still needs EXPORT permission for that module in their profile. Starts empty. Super Admin only.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Export allowed for</Label>
                <div className="mt-2 max-h-56 overflow-y-auto border rounded-sm p-2 space-y-1" data-testid="export-allowed-user-list">
                  {activeUsers.length === 0 && <p className="text-xs text-muted-foreground px-1">No active users found.</p>}
                  {activeUsers.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 px-1 py-1 text-sm rounded hover:bg-muted/50 cursor-pointer" data-testid={`export-allowed-user-${u.id}`}>
                      <input
                        type="checkbox"
                        checked={formData.export_allowed_user_ids.includes(u.id)}
                        onChange={() => toggleUserInList('export_allowed_user_ids', u.id)}
                      />
                      <span>{u.name} <span className="text-muted-foreground">({u.email})</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="capture_blur_enabled">Capture blur deterrent</Label>
                  <p className="text-xs text-muted-foreground">
                    Blurs the page while it's not in focus and briefly after PrintScreen. Switch off if it disrupts work.
                  </p>
                </div>
                <Switch
                  id="capture_blur_enabled"
                  checked={formData.capture_blur_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, capture_blur_enabled: checked }))}
                  data-testid="capture-blur-enabled-toggle"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Office Network Lock (4 Nov 2026) — built, OFF by default, Super Admin only */}
        {isSuperAdmin && (
          <Card className="rounded-sm" data-testid="network-lock-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="h-5 w-5 text-primary" />
                Office Network Lock
              </CardTitle>
              <CardDescription>
                When on, Trade IQ can only be reached from the IPs/CIDRs listed below (exempt users and Super Admins are never blocked). Off by default. Super Admin only.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="network_lock_enabled">Restrict to office network</Label>
                <Switch
                  id="network_lock_enabled"
                  checked={formData.network_lock_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, network_lock_enabled: checked }))}
                  data-testid="network-lock-enabled-toggle"
                />
              </div>
              <div>
                <Label htmlFor="allowed_networks">Allowed networks (comma-separated IPs/CIDRs)</Label>
                <Input
                  id="allowed_networks"
                  data-testid="allowed-networks-input"
                  placeholder="e.g. 203.0.113.0/24, 198.51.100.7"
                  value={formData.allowed_networks}
                  onChange={(e) => setFormData(prev => ({ ...prev, allowed_networks: e.target.value }))}
                />
              </div>
              <div>
                <Label>Exempt users</Label>
                <div className="mt-2 max-h-56 overflow-y-auto border rounded-sm p-2 space-y-1" data-testid="network-lock-exempt-user-list">
                  {activeUsers.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 px-1 py-1 text-sm rounded hover:bg-muted/50 cursor-pointer" data-testid={`network-lock-exempt-user-${u.id}`}>
                      <input
                        type="checkbox"
                        checked={formData.network_lock_exempt_user_ids.includes(u.id)}
                        onChange={() => toggleUserInList('network_lock_exempt_user_ids', u.id)}
                      />
                      <span>{u.name} <span className="text-muted-foreground">({u.email})</span></span>
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sales Order Catch-Up (Feb 2026 gap) — Super Admin / Admin only */}
        {canEditSettings && (
          <Card className="rounded-sm" data-testid="so-resync-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <RefreshCw className="h-5 w-5 text-primary" />
                Sales Order Catch-Up
              </CardTitle>
              <CardDescription>
                One-off catch-up for Sales Orders missed since 1 Feb 2026 (zoho_user_id sync bug). Always run "Check numbers" first — it makes no database changes. Super Admin / Admin only, audit-logged.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  className="rounded-sm"
                  disabled={soResyncLoading}
                  onClick={() => handleSoResync(true)}
                  data-testid="so-resync-dry-run-btn"
                >
                  {soResyncLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Check numbers (dry run)
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      className="rounded-sm"
                      disabled={soResyncLoading || !soResyncResult?.dry_run}
                      data-testid="so-resync-run-btn"
                    >
                      Run catch-up now
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Run the real Sales Order catch-up?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will upsert {soResyncResult?.new_so_count ?? '?'} new SO headers from Zoho since 1 Feb 2026
                        ({soResyncResult?.list_calls_made ?? '?'} list calls). Line-item details for these SOs follow
                        separately through the normal incremental sync, within SO's own daily cap. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleSoResync(false)} data-testid="so-resync-confirm-btn">
                        Run it
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {soResyncResult && (
                <div className="rounded-sm border border-border bg-muted/30 p-3 text-sm space-y-1" data-testid="so-resync-result">
                  <p><span className="text-muted-foreground">Mode:</span> {soResyncResult.dry_run ? 'Dry run (no writes)' : 'Real run (headers upserted)'}</p>
                  <p><span className="text-muted-foreground">New SOs found:</span> {soResyncResult.new_so_count}</p>
                  <p><span className="text-muted-foreground">Already in DB:</span> {soResyncResult.existing_so_count}</p>
                  <p><span className="text-muted-foreground">Zoho list calls used:</span> {soResyncResult.list_calls_made}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Email Log — Super Admin only */}
        {isSuperAdmin && (
          <Card className="rounded-sm max-w-none" data-testid="email-log-card">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="h-5 w-5 text-primary" />
                    Email Log
                  </CardTitle>
                  <CardDescription>
                    Every email Trade IQ has attempted to send (ZeptoMail). Super Admin only.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  className="rounded-sm"
                  disabled={sendingTestEmail}
                  onClick={handleSendTestEmail}
                  data-testid="send-test-email-btn"
                >
                  {sendingTestEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Send test email
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={emailLogFilters.status}
                  onValueChange={(value) => setEmailLogFilters((prev) => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="w-44 rounded-sm" data-testid="email-log-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All statuses</SelectItem>
                    <SelectItem value="SENT">Sent</SelectItem>
                    <SelectItem value="FAILED">Failed</SelectItem>
                    <SelectItem value="RESENT">Resent</SelectItem>
                    <SelectItem value="SKIPPED_ALLOWLIST">Skipped (allowlist)</SelectItem>
                    <SelectItem value="SKIPPED_NOT_CONFIGURED">Skipped (not configured)</SelectItem>
                    <SelectItem value="LOGGED_PREVIEW">Logged (Preview, not sent)</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={emailLogFilters.kind}
                  onValueChange={(value) => setEmailLogFilters((prev) => ({ ...prev, kind: value }))}
                >
                  <SelectTrigger className="w-40 rounded-sm" data-testid="email-log-kind-filter">
                    <SelectValue placeholder="Kind" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All kinds</SelectItem>
                    <SelectItem value="NOTIFICATION">Notification</SelectItem>
                    <SelectItem value="USER_INVITE">User invite</SelectItem>
                    <SelectItem value="TEST">Test</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Filter by notification type"
                  value={emailLogFilters.notification_type}
                  onChange={(e) => setEmailLogFilters((prev) => ({ ...prev, notification_type: e.target.value }))}
                  className="w-56 rounded-sm"
                  data-testid="email-log-type-filter"
                />
              </div>

              {emailLogLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : emailLog.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4" data-testid="email-log-empty">No emails recorded yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="email-log-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Kind</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Error</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emailLog.map((row) => (
                        <TableRow key={row.id} data-testid={`email-log-row-${row.id}`}>
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                            {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell className="text-xs">{row.kind}</TableCell>
                          <TableCell className="text-xs">{row.notification_type || '—'}</TableCell>
                          <TableCell className="text-xs">{row.to_email}</TableCell>
                          <TableCell className="text-xs max-w-[220px] truncate" title={row.subject}>{row.subject}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={EMAIL_STATUS_STYLES[row.status] || ''}>
                              {row.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[160px] truncate" title={row.error || ''}>
                            {row.error || '—'}
                          </TableCell>
                          <TableCell>
                            {row.kind === 'NOTIFICATION' && row.status === 'FAILED' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-sm"
                                disabled={resendingId === row.id}
                                onClick={() => handleResendEmail(row.id)}
                                data-testid={`resend-email-btn-${row.id}`}
                              >
                                {resendingId === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Resend'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-fit rounded-sm"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
